import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { commonStyles } from '../../../../../styles/common';

export const COLORS = {
  primary: {
    DEFAULT: 'var(--qe-primary-main)',
  },
  accent: {
    excellent: 'var(--qe-score-excellent)',
    good: 'var(--qe-score-good)',
    moderate: 'var(--qe-score-fair)',
    poor: 'var(--qe-score-poor)',
  },
  neutral: {
    grey: 'var(--qe-border-color)',
    dark: 'var(--qe-text-primary)',
    light: 'var(--qe-border-color)',
    text: 'var(--qe-text-secondary)',
    surface: 'var(--qe-surface)',
    subtleSurface: 'var(--qe-surface-subtle)',
    iconOnWhite: 'var(--qe-icon-on-white-bg)',
  },
  status: {
    success: 'var(--qe-score-good)',
    warning: 'var(--qe-score-moderate)',
    error: 'var(--qe-score-poor)',
  },
};

export const SPACING = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '24px',
  '2xl': '32px',
};

export const TYPOGRAPHY = {
  fontFamily: 'Calibre-R-Regular, -apple-system, Roboto, Helvetica, sans-serif',
  sizes: {
    xs: '8px',
    sm: '12px',
    base: '14px',
    lg: '16px',
    xl: '18px',
    '2xl': '20px',
  },
  weights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
};

export const CHART = {
  MAX_VALUE: 100,
  GRID_LINES: [
    { label: '100', value: 100 },
    { label: '80', value: 80 },
    { label: '60', value: 60 },
    { label: '40', value: 40 },
    { label: '20', value: 20 },
    { label: '0', value: 0 },
  ],
  CAP_HEIGHT_PX: 20,
  CHART_HEIGHT_PX: 140,
  LABEL_HEIGHT_PX: 18,
  BAR_WIDTH_PX: 50,
};

export const SHADOW = {
  sm: '0 1px 4px rgba(26, 26, 26, 0.20)',
};

const BASE_CARD_CLASSES = {
  baseCardRoot: {
    backgroundColor: 'var(--qe-surface)',
    boxSizing: 'border-box' as const,
    border: 'var(--qe-border-color)',
  },
};

const QE_SUMMARY_CLASSES = {
  qeScoreStatusCard: {
    boxShadow: 'none',
    backgroundColor: 'transparent',
  },
  qeScoreStatusContent: {
    padding: '8px',
  },
  qeScoreStatusTitle: {
    color: COLORS.neutral.dark,
  },
  qeScoreStatusLabel: {
    color: COLORS.neutral.dark,
    fontSize: '1.1rem',
  },
  qeScoreBadgeExcellent: {
    backgroundColor: 'var(--qe-score-excellent)',
    color: '#fff',
  },
  qeScoreBadgeGood: {
    backgroundColor: 'var(--qe-score-good)',
    color: '#1B1B1B',
  },
  qeScoreBadgeFair: {
    backgroundColor: 'var(--qe-score-fair)',
    color: '#1B1B1B',
  },
  qeScoreBadgePoor: {
    backgroundColor: 'var(--qe-score-poor)',
    color: '#fff',
  },
  qeScoreBadgeVeryPoor: {
    backgroundColor: 'var(--qe-score-very-poor)',
    color: '#fff',
  },
  qeTrendBadgeNotApplicable: {
    backgroundColor: 'var(--qe-score-not-applicable)',
  },
  disabledSparkle: {
    filter: 'grayscale(1)',
  },
  analysisButton: {
    letterSpacing: '0.6px',
    fontSize: '1rem',
    textTransform: 'none' as const,
    fontWeight: 600,
    padding: '8px 18px',
  },
  analysisHintText: {
    fontStyle: 'italic',
    fontSize: '14px',
    color: 'var(--qe-report-info-text-color)',
    textAlign: 'right' as const,
    lineHeight: 1.4,
  },
  analyzeButtonIcon: {
    color: 'var(--qe-analyze-icon-color)',
  },
};

const QE_TREND_CLASSES = {
  qeTrendContainer: {
    borderColor: COLORS.neutral.light,
    backgroundColor: COLORS.neutral.surface,
    padding: 16,
  },
  qeTrendTitle: {
    color: COLORS.neutral.dark,
    fontFamily: TYPOGRAPHY.fontFamily,
  },
  qeTrendChartContainer: {
    borderColor: COLORS.neutral.light,
    backgroundColor: COLORS.neutral.surface,
  },
  qeTrendTooltipWrapper: {
    backgroundColor: COLORS.neutral.surface,
    padding: '8px 12px',
    border: `1px solid ${COLORS.neutral.light}`,
    borderRadius: '4px',
    boxShadow: 'var(--qe-tooltip-shadow)',
  },
  qeTrendTooltipTitle: {
    margin: '0 0 4px 0',
    fontSize: '12px',
    color: COLORS.neutral.text,
  },
  qeTrendTooltipValue: {
    margin: 0,
    fontSize: '14px',
    fontWeight: 600,
  },
  qeTrendScoreVeryPoor: {
    backgroundColor: 'var(--qe-score-very-poor)',
  },
  qeTrendScorePoor: {
    backgroundColor: 'var(--qe-score-poor)',
  },
  qeTrendScoreFair: {
    backgroundColor: 'var(--qe-score-fair)',
  },
  qeTrendScoreGood: {
    backgroundColor: 'var(--qe-score-good)',
  },
  qeTrendScoreExcellent: {
    backgroundColor: 'var(--qe-score-excellent)',
  },
  qeTrendLegendBox: {
    width: 15,
    height: 11,
  },
  qeTrendLegendLabel: {
    letterSpacing: '0.06px',
    fontFamily: TYPOGRAPHY.fontFamily,
    color: 'var(--qe-kpi-metric-not-applicable)',
    fontWeight: TYPOGRAPHY.weights.normal,
    whiteSpace: 'nowrap' as const,
  },
  qeTrendBarColor: {
    fill: 'var(--qe-trend-bar-color)',
  },
  qeTrendLegendNotApplicable: {
    backgroundColor: 'var(--qe-score-not-applicable)',
  },
  qeTrendLegendVeryPoor: {
    backgroundColor: 'var(--qe-score-very-poor)',
  },
  qeTrendLegendPoor: {
    backgroundColor: 'var(--qe-score-poor)',
  },
  qeTrendLegendFair: {
    backgroundColor: 'var(--qe-score-fair)',
  },
  qeTrendLegendGood: {
    backgroundColor: 'var(--qe-score-good)',
  },
  qeTrendLegendExcellent: {
    backgroundColor: 'var(--qe-score-excellent)',
  },
};

const QE_BREAKDOWN_CLASSES = {
  qeBreakdownContainer: {
    backgroundColor: COLORS.neutral.surface,
    borderColor: COLORS.neutral.light,
    padding: '0px 16px',
  },
  qeBreakdownTitle: {
    color: COLORS.neutral.dark,
  },
  qeBreakdownLegendLabel: {
    color: COLORS.neutral.dark,
  },
  qeBreakdownLegend: {
    border: `1px solid ${COLORS.neutral.light}`,
    borderRadius: '4px',
    padding: '8px 12px',
  },
  qeBreakdownSuccessIcon: {
    color: COLORS.accent.excellent,
    fontSize: 20,
  },
  qeBreakdownErrorIcon: {
    color: 'var(--qe-score-poor)',
    fontSize: 20,
  },
  qeBreakdownMobileDivider: {
    borderColor: COLORS.neutral.light,
  },
  kpiMetricRow: {
    borderBottom: `1px solid ${COLORS.neutral.light}`,
  },
  kpiMetricProcess: {
    color: COLORS.neutral.text,
  },
  kpiMetricScoreValue: {
    color: COLORS.neutral.dark,
  },
  kpiMetricNotApplicableValue: {
    color: 'var(--qe-kpi-metric-not-applicable)',
  },
  kpiMetricCompliantIcon: {
    color: COLORS.accent.excellent,
    fontSize: 20,
  },
  kpiMetricNonCompliantIcon: {
    color: 'var(--qe-score-poor)',
    fontSize: 20,
  },
};

const KPI_OVERVIEW_CLASSES = {
  kpiOverviewSection: {
    borderTop: `1px solid ${COLORS.neutral.light}`,
  },
  kpiOverviewTitle: {
    color: COLORS.neutral.dark,
  },
  kpiOverviewCard: {
    backgroundColor: COLORS.neutral.surface,
    borderColor: COLORS.neutral.light,
    padding: '8px',
  },
  kpiOverviewCardTitle: {
    color: COLORS.neutral.dark,
  },
  kpiOverviewCardValue: {
    color: COLORS.neutral.text,
  },
};

const RECOMMENDED_ACTIONS_CLASSES = {
  recommendedActionsContainer: {
    padding: '0px',
    gap: '3px',
    marginBottom: 24,
  },
  recommendedActionsTitle: {
    color: COLORS.neutral.dark,
  },
  recommendedActionsEmptyStateText: {
    color: COLORS.neutral.text,
  },
  recommendedActionsSearchIcon: {
    color: COLORS.neutral.iconOnWhite,
    fontSize: 18,
    transform: 'translateY(-50%)',
  },
  recommendedActionsSearchInput: {
    borderColor: COLORS.neutral.light,
    outlineColor: COLORS.primary.DEFAULT,
    '& .MuiOutlinedInput-root': {
      height: 48,
    },
  },
  recommendedActionsSelect: {
    borderColor: COLORS.neutral.light,
    background: 'var(--qe-surface)',
    width: 260,
    maxWidth: '100%',
    '& .MuiOutlinedInput-root': {
      height: 48,
    },
    '& .MuiSelect-outlined': {
      paddingTop: 12.5,
      paddingBottom: 12.5,
      whiteSpace: 'nowrap' as const,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  recommendedActionsSelectLabel: {
    color: COLORS.primary.DEFAULT,
    fontSize: 14,
    lineHeight: '16px',
    marginBottom: 2,
    marginLeft: 10,
  },
  recommendedActionsMenuItem: {
    '&.Mui-selected': {
      backgroundColor: 'transparent',
    },
    '&.Mui-selected:hover': {
      backgroundColor: 'transparent',
    },
  },
  recommendedActionsExpandIcon: {
    color: COLORS.neutral.dark,
    backgroundColor: COLORS.neutral.surface,
    fontSize: 18,
    transform: 'translateY(-50%)',
  },
  actionCard: {
    background: 'var(--qe-score-card-background)',
  },

  actionCardTitle: {
    color: COLORS.neutral.text,
    fontWeight: 400,
  },

  actionTypeLabelChip: {
    backgroundColor: 'var(--qe-action-label-bg)',
    color: COLORS.neutral.text,
    fontSize: TYPOGRAPHY.sizes.sm,
    '& .MuiChip-label': {
      display: 'flex',
      alignItems: 'center',
      padding: '0 4px',
    },
  },

  actionCardCountChip: {
    backgroundColor: 'var(--qe-action-count-bg)',
    color: COLORS.neutral.text,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 18,
    height: 18,
    padding: '0 5px',
    borderRadius: 9,
    fontWeight: 600,
    fontSize: TYPOGRAPHY.sizes.sm,
    lineHeight: '18px',
  },
  actionCardPriorityHigh: {
    backgroundColor: '#F76A51',
    color: '#fff',
    fontWeight: 600,
  },
  actionCardPriorityMedium: {
    backgroundColor: '#FF9A10',
    color: '#000',
    fontWeight: 600,
  },
  actionCardPriorityLow: {
    backgroundColor: 'var(--qe-score-excellent)',
    color: '#fff',
    fontWeight: 600,
  },
  actionFooterNoticeBox: {
    color: 'var(--qe-action-footer-notice-text-color)',
    display: 'block',
    width: '85%',
    maxWidth: '85%',
    boxSizing: 'border-box' as const,
    padding: '6px 2px',
  },

  actionFooterBoldText: {
    fontWeight: 600,
    fontSize: '14px',
  },
  actionFooterBodyText: {
    fontWeight: 100,
    fontSize: '13px',
  },
  actionFooterButton: {
    textTransform: 'none' as const,
    marginLeft: 'auto',
    width: 120,
    minWidth: 100,
    flexShrink: 0,
  },
};

const WORK_ITEMS_CLASSES = {
  workItemsModalTotalText: {
    marginRight: 'auto',
    paddingLeft: 8,
  },
  workItemsTable: {
    borderColor: COLORS.neutral.light,
  },
  workItemsTableHeadRow: {
    backgroundColor: 'var(--qe-surface-subtle)',
  },
  workItemsTableHeadCell: {
    color: COLORS.neutral.text,
    borderColor: COLORS.neutral.light,
  },
  workItemsTableBodyRow: {
    borderColor: COLORS.neutral.light,
    '&:hover': {
      backgroundColor: 'var(--qe-hover-bg)',
    },
  },
  workItemsTableLink: {
    color: COLORS.neutral.text,
  },
  noWorkItemsMessage: {
    color: COLORS.neutral.text,
    padding: 16,
    textAlign: 'center' as const,
  },
};

const MODAL_ACTION_CLASSES = {
  modalActionCard: {
    background: 'var(--qe-score-card-background)',
    borderColor: COLORS.neutral.grey,
    border: '1px solid',
    padding: '8px',
    gap: '3px',
  },
  modalActionTitle: {
    color: COLORS.neutral.text,
    fontWeight: 500,
  },
  modalActionCountChip: {
    backgroundColor: COLORS.neutral.light,
    fontWeight: 500,
    color: COLORS.neutral.text,
  },

  modalActionPriorityHigh: {
    backgroundColor: COLORS.accent.poor,
    color: '#fff',
    fontWeight: 600,
  },
  modalActionPriorityMedium: {
    backgroundColor: COLORS.accent.moderate,
    color: '#000',
    fontWeight: 600,
  },
  modalActionPriorityLow: {
    backgroundColor: COLORS.accent.good,
    color: '#fff',
    fontWeight: 600,
  },
  modalActionBox: {
    color: '#393C3D',
    display: 'inline-block',
    maxWidth: 'fit-content',
    padding: '6px 12px',
  },
  modalActionLabel: {
    fontWeight: 600,
  },
  modalActionText: {
    fontWeight: 100,
    fontSize: '13px',
  },
};

const WORK_LOG_CLASSES = {
  workLogContainer: {
    backgroundColor: COLORS.neutral.surface,
    border: `1px solid ${COLORS.neutral.grey}`,
    boxSizing: 'border-box' as const,
  },
  workLogScrollContainer: {
    height: '500px',
  },
  workLogEmptyStateText: {
    color: COLORS.neutral.text,
  },
  workLogRow: {
    backgroundColor: 'transparent',
    borderBottom: `1px solid ${COLORS.neutral.light}`,
    boxSizing: 'border-box' as const,
  },
  workLogEntryId: {
    color: COLORS.neutral.dark,
  },
  workLogEntryDescription: {
    color: COLORS.neutral.text,
  },
  workLogLoaderDot: {
    backgroundColor: COLORS.primary.DEFAULT,
  },
  workLogLoaderDotDelayed200: {
    backgroundColor: COLORS.primary.DEFAULT,
    animationDelay: '0.2s',
  },
  workLogLoaderDotDelayed400: {
    backgroundColor: COLORS.primary.DEFAULT,
    animationDelay: '0.4s',
  },
  workLogEntry: {
    animation: '$slideIn 0.2s ease-out forwards',
  },

  '@keyframes slideIn': {
    from: { opacity: 0, transform: 'translateY(-6px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
};

const NOT_APPLICABLE_CLASSES = (theme: Theme) => ({
  notApplicableRoot: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6, 2),
    minHeight: 400,
    textAlign: 'center' as const,
  },
  notApplicableIconWrapper: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
    lineHeight: 0,
  },
  notApplicableTitle: {
    color: theme.palette.primary.main,
    fontFamily: theme.typography.fontFamily,
    fontWeight: TYPOGRAPHY.weights.semibold,
    fontSize: '1.25rem',
    marginBottom: theme.spacing(1.5),
  },
  notApplicableDivider: {
    width: 560,
    maxWidth: '90%',
    borderBottom: `${theme.palette.divider} 1px solid`,
    marginBottom: theme.spacing(2),
  },
  notApplicableBody: {
    color: theme.palette.text.muted,
    fontFamily: theme.typography.fontFamily,
    fontSize: TYPOGRAPHY.sizes.base,
    lineHeight: 1.6,
    marginBottom: theme.spacing(0.75),
  },
  notApplicableLink: {
    color: theme.palette.primary.main,
    fontFamily: theme.typography.fontFamily,
    textDecoration: 'underline',
  },
});

const NEXT_ACTION_ALERT_CLASSES = (theme: Theme) => ({
  nextActionAlertBox: {
    color: 'var(--qe-action-next-action-text-color)',

    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box' as const,
    padding: '2px 4px',
    borderRadius: 3,
    display: 'flex' as const,
    alignItems: 'center',
    gap: 8,
    height: 'fit-content',
    [theme.breakpoints.up('sm')]: {
      width: 'auto',
      maxWidth: '48%',
    },
    '& a': {
      color: theme.palette.text.secondary,
      textDecoration: 'underline',
    },
  },
  nextActionAlertIcon: {
    color: 'var(--qe-action-next-action-text-color)',
    fontSize: 16,
    flexShrink: 0,
  },
  nextActionAlertText: {
    fontSize: '13px',
    lineHeight: '18px',
  },
});

const INDEX_PAGE_CLASSES = {
  indexPageContent: {
    padding: 16,
  },
  indexTabsPaper: {
    borderColor: COLORS.neutral.light,
    marginTop: 16,
  },
  indexReportInfoRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '12px 16px 10px',
    minHeight: 40,
  },
  indexReportInfoText: {
    color: 'var(--qe-report-info-text-color)',
    fontSize: '14px',
    fontStyle: 'italic',
    fontFamily: 'Calibre-R-Regular',
    lineHeight: '16px',
    textAlign: 'right' as const,
  },

  indexTabsHeader: {
    borderBottom: `1px solid ${COLORS.neutral.grey}`,
  },

  indexTabsContainer: {
    display: 'flex' as const,
    gap: 4,
    borderBottom: `2px solid ${COLORS.neutral.grey}`,
    padding: '0 24px',
    overflowX: 'auto' as const,
    overflowY: 'hidden' as const,
    scrollbarWidth: 'none' as const,
    '&::-webkit-scrollbar': {
      width: 0,
      height: 0,
    },
  },

  indexTabButtonBase: {
    padding: '12px 24px',
    background: 'transparent',
    border: 'none',
    borderBottom: '3px solid transparent',
    color: COLORS.primary.DEFAULT,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer' as const,
    position: 'relative' as const,
    bottom: -1,
    textTransform: 'none' as const,
    borderRadius: 0,
    whiteSpace: 'nowrap' as const,
  },
  indexTabButtonBlocked: {
    cursor: 'not-allowed !important' as const,
    opacity: 0.5,
  },

  indexTabButtonActive: {
    color: COLORS.primary.DEFAULT,
    fontWeight: 600,
    borderBottom: `3px solid ${COLORS.primary.DEFAULT}`,
  },
  indexTabButtonInactive: {
    color: COLORS.neutral.text,
    borderBottom: '3px solid transparent',
  },
  indexWorkLogSearch: {
    minWidth: 220,
    padding: 10,
  },
  indexSearchIcon: {
    color: COLORS.neutral.text,
  },
  indexDownloadButton: {
    color: COLORS.primary.DEFAULT,
    borderColor: COLORS.primary.DEFAULT,
    textTransform: 'none' as const,
    marginRight: 10,
    marginBottom: 12,
  },
  indexPageLoadStatusMessage: {
    marginTop: 12,
    color: 'var(--qe-report-info-text-color)',
    fontSize: TYPOGRAPHY.sizes.base,
    fontStyle: 'italic',
  },
  indexBusyCursor: {
    '&, & *': {
      cursor: 'wait !important' as const,
    },
  },
  indexLoadingSpinner: {
    color: COLORS.primary.DEFAULT,
    marginBottom: 16,
  },
  indexAnalysingText: {
    color: COLORS.neutral.dark,
  },
  indexAnalysisSummaryGrid: {
    marginBottom: 24,
  },
  indexAnalysisSummaryPaper: {
    padding: 16,
    background: 'var(--qe-score-card-background)',
  },
  indexAnalysisSummaryTitle: {
    color: COLORS.neutral.dark,
    marginBottom: 8,
  },
  indexAnalysisSummaryValue: {
    color: COLORS.neutral.text,
    fontWeight: 600,
  },
  indexAnalysisScoreTrend: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0,
  },
  indexAnalysisScoreUpIcon: {
    color: 'var(--qe-score-excellent)',
    fontSize: 20,
    transform: 'scaleX(0.72)',
    transformOrigin: 'center',
    marginLeft: 2,
    marginRight: 0,
  },
  indexAnalysisScoreImprovement: {
    color: 'var(--qe-score-excellent)',
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: 0,
    fontSize: TYPOGRAPHY.sizes.base,
    lineHeight: 1,
  },
  estimatedTimeBox: {
    marginBottom: 16,
    padding: 16,
    borderRadius: 4,
    backgroundColor: 'var(--qe-info-bg)',
    border: '1px solid var(--qe-info-border)',
  },
  tableHeaderRow: {
    backgroundColor: 'var(--qe-surface-subtle)',
  },
  tableHeaderCell: {
    color: COLORS.neutral.text,
    fontSize: '12px',
    fontWeight: 500,
    borderColor: COLORS.neutral.light,
  },
  tableBodyCell: {
    color: COLORS.neutral.text,
    fontSize: '12px',
  },
  goodScore: {
    color: COLORS.accent.good,
    fontWeight: 600,
  },
  estimatedTimeText: {
    color: 'var(--qe-info-text)',
    fontWeight: 500,
  },
  boldText: {
    fontWeight: 600,
  },

  zeroScoreError: {
    color: 'var(--qe-primary-main)',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '32px',
  },
};

const QE_THEME_SCOPE_CLASSES = (theme: Theme) => ({
  qeThemeScope: {
    '--qe-primary-main': theme.palette.primary.main,
    '--qe-surface': theme.palette.background.paper,
    '--qe-surface-subtle':
      theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.06)' : '#e5e7eb',
    '--qe-border-color': theme.palette.border,
    '--qe-text-primary': theme.palette.type === 'dark' ? '#fff' : '#000',
    '--qe-text-secondary': theme.palette.text.secondary,
    '--qe-tooltip-shadow':
      theme.palette.type === 'dark'
        ? '0 2px 8px rgba(0, 0, 0, 0.45)'
        : '0 2px 8px rgba(0, 0, 0, 0.1)',
    '--qe-info-bg':
      theme.palette.type === 'dark' ? 'rgba(144, 202, 249, 0.12)' : '#EFF6FF',
    '--qe-info-border':
      theme.palette.type === 'dark' ? 'rgba(144, 202, 249, 0.28)' : '#BFDBFE',
    '--qe-info-text': theme.palette.type === 'dark' ? '#90CAF9' : '#1E3A8A',
    '--qe-hover-bg': theme.palette.action.hover,
    '--qe-trend-bar-color':
      theme.palette.type === 'dark' ? '#B5BFC1' : '#CAD1D3',
    '--qe-action-label-bg':
      theme.palette.type === 'dark'
        ? 'rgba(255,255,255,0.12)'
        : 'rgba(0,0,0,0.10)',
    '--qe-action-count-bg':
      theme.palette.type === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.26)',
    '--qe-score-very-poor': '#885073',
    '--qe-score-poor': '#F76A51',
    '--qe-score-good': '#3E7CA6',
    '--qe-score-fair': '#FF9A10',
    '--qe-score-excellent': '#50A578',
    '--qe-score-not-applicable':
      theme.palette.type === 'dark' ? '#B5BFC1' : '#666B6D',
    '--qe-kpi-metric-not-applicable':
      theme.palette.type === 'dark' ? '#B5BFC1' : '#666B6D',
    '--qe-score-card-background':
      theme.palette.type === 'dark' ? '#595959' : '1px solid #EDEEEF',
    '--qe-action-footer-notice-bg':
      theme.palette.type === 'dark' ? '#4D3F02' : '#FCF7E5',
    '--qe-action-footer-notice-text-color':
      theme.palette.type === 'dark' ? '#fff' : '#000',
    '--qe-action-next-action-text-color':
      theme.palette.type === 'dark' ? '#DBD99A' : theme.palette.error.main,
    '--qe-analyze-icon-color': theme.palette.type === 'dark' ? '#fff' : '#000',
    '--qe-report-info-text-color':
      theme.palette.type === 'dark' ? '#fff' : '#7B8083',
    '--qe-icon-on-white-bg': '#757575',
  } as Record<string, string>,
});

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    ...commonStyles(theme),
    ...QE_THEME_SCOPE_CLASSES(theme),
    ...BASE_CARD_CLASSES,
    ...QE_SUMMARY_CLASSES,
    ...QE_TREND_CLASSES,
    ...QE_BREAKDOWN_CLASSES,
    ...KPI_OVERVIEW_CLASSES,
    ...RECOMMENDED_ACTIONS_CLASSES,
    ...WORK_ITEMS_CLASSES,
    ...MODAL_ACTION_CLASSES,
    ...WORK_LOG_CLASSES,
    ...INDEX_PAGE_CLASSES,
    ...NOT_APPLICABLE_CLASSES(theme),
    ...NEXT_ACTION_ALERT_CLASSES(theme),
  }),
);
